/* De bewerkers voor de samengestelde blokken van Websitecontent (ADR 0024): het lijstje, de
 * tekstvakken rechts en de voordelen. Dit bestand tekent alleen. Wat een geldige vorm is, wat er bij
 * een wijziging met de JSON gebeurt en wanneer een blok wordt geschreven, staat in data/blokken.ts.
 *
 * Een bewerker houdt geen eigen kopie bij: hij krijgt de JSON uit het concept en geeft bij elke
 * wijziging de nieuwe JSON terug, waarin alleen het aangeraakte onderdeel anders is. Herkent
 * leesBlok de vorm in de database niet, dan staat het blok er alleen-lezen, met de reden erbij. */

import * as Lucide from 'lucide-react';
import { ArrowDown, ArrowUp, Plus, RotateCcw, Trash2 } from 'lucide-react';
import type { LucideIcon } from 'lucide-react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import {
  ICONEN, STANDAARD_ICOON, leesBlok, leesTekstvak, leesVoordeel, metNieuwOnderdeel, metOnderdeel, nieuwOnderdeel,
  verplaatst, zelfdeBlok, zetTekstvak, zetVoordeel, zonderOnderdeel, type Blok, type Tekstvak, type Voordeel,
} from './data/blokken';

/* De site zoekt een icoon op naam in lucide-react: eerst exact, daarna zonder op hoofdletters te
 * letten (getIcon in de site-repo). De CRM heeft dezelfde versie van lucide-react, dus dezelfde
 * opzoeking zegt hier of de site een naam kent. */
const BEELD: Record<string, LucideIcon> = {};
const BEELD_KLEIN: Record<string, LucideIcon> = {};
for (const [naam, waarde] of Object.entries(Lucide as Record<string, unknown>)) {
  if (naam === 'createLucideIcon' || naam === 'default' || naam.startsWith('__')) continue;
  if (typeof waarde === 'function' || (typeof waarde === 'object' && waarde !== null && '$$typeof' in waarde)) {
    BEELD[naam] = waarde as LucideIcon;
    BEELD_KLEIN[naam.toLowerCase()] = waarde as LucideIcon;
  }
}

function icoonVan(naam: string): LucideIcon | null {
  const schoon = naam.trim();
  return schoon ? BEELD[schoon] ?? BEELD_KLEIN[schoon.toLowerCase()] ?? null : null;
}

function IcoonRegel({ naam }: { naam: string }) {
  const Beeld = icoonVan(naam);
  return (
    <span className="flex min-w-0 items-center gap-2">
      {Beeld ? <Beeld className="h-4 w-4 shrink-0" /> : <span className="h-4 w-4 shrink-0 rounded-sm border border-dashed" />}
      <span className="truncate">{naam}</span>
    </span>
  );
}

/* Een naam die niet in de keuzelijst staat, blijft staan en is als zodanig te zien. Hij verandert
 * alleen als iemand zelf een ander icoon kiest. */
function IcoonKeuze({ id, waarde, standaard, uit, zet }: { id: string; waarde: string; standaard: string; uit: boolean; zet: (naam: string) => void }) {
  const huidig = waarde.trim();
  const inLijst = ICONEN.includes(huidig);
  const bekend = !!icoonVan(huidig);
  return (
    <div className="space-y-1.5">
      <Label htmlFor={id}>Icoon</Label>
      <Select value={huidig || undefined} onValueChange={zet} disabled={uit}>
        <SelectTrigger id={id} data-icoon={huidig}><SelectValue placeholder={`Standaard (${standaard})`} /></SelectTrigger>
        <SelectContent className="max-h-72">
          {huidig && !inLijst && <SelectItem value={huidig}><IcoonRegel naam={huidig} /></SelectItem>}
          {ICONEN.map((naam) => <SelectItem key={naam} value={naam}><IcoonRegel naam={naam} /></SelectItem>)}
        </SelectContent>
      </Select>
      {huidig && !inLijst && bekend && (
        <p className="text-xs text-muted-foreground" data-icoon-melding="buiten-lijst">Dit icoon staat niet in de keuzelijst. Het blijft staan tot je een ander kiest.</p>
      )}
      {huidig && !bekend && (
        <p className="text-xs text-amber-600" data-icoon-melding="onbekend">De site kent het icoon "{huidig}" niet en toont daarom het standaardicoon ({standaard}). Kies een icoon uit de lijst, of laat het zo.</p>
      )}
    </div>
  );
}

function Knoppen({ wat, i, aantal, uit, omhoog, omlaag, weg }: { wat: string; i: number; aantal: number; uit: boolean; omhoog: () => void; omlaag: () => void; weg: () => void }) {
  return (
    <div className="flex shrink-0 justify-end gap-1">
      <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`${wat} omhoog`} title="Omhoog" disabled={uit || i === 0} onClick={omhoog}><ArrowUp /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`${wat} omlaag`} title="Omlaag" disabled={uit || i === aantal - 1} onClick={omlaag}><ArrowDown /></Button>
      <Button variant="ghost" size="icon" className="h-8 w-8" aria-label={`${wat} weghalen`} title="Weghalen" disabled={uit} onClick={weg}><Trash2 /></Button>
    </div>
  );
}

/* Waar de browser het kan, groeit een invoervak mee met zijn tekst; anders gelden de rijen. */
const GROEIT = '[field-sizing:content]';

/* Een rij korte zinnen: het lijstje zelf, en de punten in een tekstvak. */
function Punten({ naam, punten, uit, zet }: { naam: string; punten: string[]; uit: boolean; zet: (punten: string[]) => void }) {
  return (
    <div className="space-y-2">
      {punten.length === 0 && <p className="text-sm text-muted-foreground">Nog geen punten.</p>}
      {punten.length > 0 && (
        <ol className="space-y-2">
          {punten.map((punt, i) => (
            <li key={i} className="flex flex-col gap-1 sm:flex-row sm:items-start sm:gap-2">
              <div className="flex min-w-0 flex-1 items-start gap-2">
                <span className="mt-2 w-5 shrink-0 text-right text-xs text-muted-foreground">{i + 1}</span>
                <Textarea aria-label={`${naam} ${i + 1}`} rows={2} className={`min-h-0 ${GROEIT}`} value={punt} disabled={uit} onChange={(e) => zet(metOnderdeel(punten, i, e.target.value) as string[])} />
              </div>
              <Knoppen wat={`${naam} ${i + 1}`} i={i} aantal={punten.length} uit={uit}
                omhoog={() => zet(verplaatst(punten, i, -1) as string[])}
                omlaag={() => zet(verplaatst(punten, i, 1) as string[])}
                weg={() => zet(zonderOnderdeel(punten, i) as string[])} />
            </li>
          ))}
        </ol>
      )}
      <Button variant="outline" size="sm" disabled={uit} onClick={() => zet(metNieuwOnderdeel(punten, '') as string[])}><Plus /> Punt toevoegen</Button>
    </div>
  );
}

interface BewerkerProps { blok: Blok; onderdelen: unknown[]; uit: boolean; zet: (waarde: unknown[]) => void }

function Onderdeel({ wat, i, aantal, uit, zet, onderdelen, children }: { wat: string; i: number; aantal: number; uit: boolean; zet: (waarde: unknown[]) => void; onderdelen: unknown[]; children: React.ReactNode }) {
  return (
    <li className="space-y-3 rounded-md border p-3" data-onderdeel={i + 1}>
      <div className="flex items-center justify-between gap-2">
        <span className="text-sm font-medium">{wat}</span>
        <Knoppen wat={wat} i={i} aantal={aantal} uit={uit}
          omhoog={() => zet(verplaatst(onderdelen, i, -1))}
          omlaag={() => zet(verplaatst(onderdelen, i, 1))}
          weg={() => zet(zonderOnderdeel(onderdelen, i))} />
      </div>
      {children}
    </li>
  );
}

function Toevoegen({ blok, onderdelen, uit, zet }: BewerkerProps) {
  return (
    <Button variant="outline" size="sm" disabled={uit} onClick={() => zet(metNieuwOnderdeel(onderdelen, nieuwOnderdeel(blok.soort, onderdelen)))}>
      <Plus /> {blok.onderdeel} toevoegen
    </Button>
  );
}

function VoordelenBewerker({ blok, onderdelen, uit, zet }: BewerkerProps) {
  return (
    <div className="space-y-3">
      {onderdelen.length === 0 && <p className="text-sm text-muted-foreground">Nog geen voordelen.</p>}
      {onderdelen.length > 0 && (
        <ol className="space-y-3">
          {onderdelen.map((onderdeel, i) => {
            const voordeel = leesVoordeel(onderdeel);
            const id = `${blok.kolom}-${i}`;
            const wijzig = (veld: keyof Voordeel, waarde: string) => zet(metOnderdeel(onderdelen, i, zetVoordeel(onderdeel, veld, waarde)));
            return (
              <Onderdeel key={i} wat={`${blok.onderdeel} ${i + 1}`} i={i} aantal={onderdelen.length} uit={uit} zet={zet} onderdelen={onderdelen}>
                <div className="grid gap-3 sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]">
                  <IcoonKeuze id={`${id}-icoon`} waarde={voordeel.icoon} standaard={STANDAARD_ICOON.voordelen} uit={uit} zet={(naam) => wijzig('icoon', naam)} />
                  <div className="space-y-1.5">
                    <Label htmlFor={`${id}-titel`}>Titel</Label>
                    <Input id={`${id}-titel`} value={voordeel.titel} disabled={uit} onChange={(e) => wijzig('titel', e.target.value)} />
                  </div>
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor={`${id}-zin`}>Zin onder de titel</Label>
                  <Textarea id={`${id}-zin`} rows={2} className={`min-h-0 ${GROEIT}`} value={voordeel.zin} disabled={uit} onChange={(e) => wijzig('zin', e.target.value)} />
                </div>
              </Onderdeel>
            );
          })}
        </ol>
      )}
      <Toevoegen blok={blok} onderdelen={onderdelen} uit={uit} zet={zet} />
    </div>
  );
}

/* Wat er bij de inhoud van een tekstvak hoort, per vorm (zie leesTekstvak). */
const VORM_UITLEG: Record<Tekstvak['vorm'], string> = {
  content: 'De site toont eerst de tekst en daaronder de punten met een vinkje. Een van de twee mag leeg blijven. Het teken | kan hier niet in staan: de site gebruikt het om de punten te scheiden.',
  items: 'Dit vak bestaat alleen uit punten. Wil je er een tekst boven, maak dan een nieuw tekstvak.',
  text: 'Dit vak bestaat alleen uit tekst. Wil je er punten onder, maak dan een nieuw tekstvak.',
};

function TekstvakkenBewerker({ blok, onderdelen, uit, zet }: BewerkerProps) {
  return (
    <div className="space-y-3">
      {onderdelen.length === 0 && <p className="text-sm text-muted-foreground">Nog geen tekstvakken.</p>}
      {onderdelen.length > 0 && (
        <ol className="space-y-3">
          {onderdelen.map((onderdeel, i) => {
            const vak = leesTekstvak(onderdeel);
            const id = `${blok.kolom}-${i}`;
            const wat = `${blok.onderdeel} ${i + 1}`;
            const wijzig = (wijziging: Parameters<typeof zetTekstvak>[1]) => zet(metOnderdeel(onderdelen, i, zetTekstvak(onderdeel, wijziging)));
            return (
              <Onderdeel key={i} wat={wat} i={i} aantal={onderdelen.length} uit={uit} zet={zet} onderdelen={onderdelen}>
                <div className="grid gap-3 sm:grid-cols-[minmax(0,15rem)_minmax(0,1fr)]">
                  <IcoonKeuze id={`${id}-icoon`} waarde={vak.icoon} standaard={STANDAARD_ICOON.tekstvakken} uit={uit} zet={(naam) => wijzig({ icoon: naam })} />
                  <div className="space-y-1.5">
                    <Label htmlFor={`${id}-titel`}>Titel</Label>
                    <Input id={`${id}-titel`} value={vak.titel} disabled={uit} onChange={(e) => wijzig({ titel: e.target.value })} />
                  </div>
                </div>
                {vak.vorm !== 'items' && (
                  <div className="space-y-1.5">
                    <Label htmlFor={`${id}-tekst`}>Tekst</Label>
                    <Textarea id={`${id}-tekst`} rows={4} className={GROEIT} value={vak.tekst} disabled={uit} onChange={(e) => wijzig({ tekst: e.target.value })} />
                  </div>
                )}
                {vak.vorm !== 'text' && (
                  <div className="space-y-1.5">
                    <p className="text-sm font-medium leading-none">Punten</p>
                    <Punten naam={`${wat} punt`} punten={vak.punten} uit={uit} zet={(punten) => wijzig({ punten })} />
                  </div>
                )}
                <p className="text-xs text-muted-foreground">{VORM_UITLEG[vak.vorm]}</p>
              </Onderdeel>
            );
          })}
        </ol>
      )}
      <Toevoegen blok={blok} onderdelen={onderdelen} uit={uit} zet={zet} />
    </div>
  );
}

function LijstBewerker({ blok, onderdelen, uit, zet }: BewerkerProps) {
  return (
    <div className="space-y-2">
      <Punten naam={blok.onderdeel} punten={onderdelen as string[]} uit={uit} zet={zet} />
      <p className="text-xs text-muted-foreground">Opmaak is HTML: {'<strong>vet</strong>'} wordt vet. Een los teken {'<'} leest de site ook als opmaak.</p>
    </div>
  );
}

const BEWERKER: Record<Blok['soort'], (props: BewerkerProps) => JSX.Element> = {
  lijst: LijstBewerker,
  tekstvakken: TekstvakkenBewerker,
  voordelen: VoordelenBewerker,
};

/** Eén blok in het formulier. `opgeslagen` is de inhoud in de database (bij iets nieuws niets),
 *  `waarde` die in het concept. Zijn ze gelijk, dan wordt het blok bij opslaan niet geschreven. */
export function BlokKaart({ blok, opgeslagen, waarde, fout, uit, zet }: { blok: Blok; opgeslagen: unknown; waarde: unknown; fout?: string; uit: boolean; zet: (waarde: unknown) => void }) {
  const basis = leesBlok(blok.soort, opgeslagen);
  const lezing = basis.ok ? leesBlok(blok.soort, waarde) : basis;

  if (!lezing.ok) {
    return (
      <Card data-blok={blok.kolom} data-blok-staat="alleen-lezen">
        <CardHeader className="pb-2">
          <CardTitle className="flex flex-wrap items-center gap-2 text-base">{blok.label} <Badge variant="secondary">Alleen lezen</Badge></CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-muted-foreground">
            Dit blok heeft een vorm die de bewerker niet kent. {lezing.reden} Daarom is het hier niet te wijzigen. Het blijft bij opslaan ongewijzigd.
          </p>
          <pre className="max-h-64 overflow-auto whitespace-pre-wrap rounded-md border p-3 text-xs text-muted-foreground">{JSON.stringify(opgeslagen ?? null, null, 2)}</pre>
        </CardContent>
      </Card>
    );
  }

  const gewijzigd = !zelfdeBlok(opgeslagen, waarde);
  const Bewerker = BEWERKER[blok.soort];
  return (
    <Card data-blok={blok.kolom} data-blok-staat={gewijzigd ? 'gewijzigd' : 'ongewijzigd'}>
      <CardHeader className="pb-2">
        <div className="flex flex-wrap items-center justify-between gap-2">
          <CardTitle className="text-base">{blok.label}</CardTitle>
          {gewijzigd && (
            <div className="flex flex-wrap items-center gap-2">
              <Badge variant="secondary">Gewijzigd, nog niet opgeslagen</Badge>
              <Button variant="ghost" size="sm" disabled={uit} onClick={() => zet(opgeslagen)}><RotateCcw /> Terugzetten</Button>
            </div>
          )}
        </div>
        <p className="text-sm text-muted-foreground">{blok.uitleg}</p>
      </CardHeader>
      <CardContent className="space-y-3">
        <Bewerker blok={blok} onderdelen={lezing.onderdelen} uit={uit} zet={zet} />
        {fout && <p className="text-xs text-destructive" role="alert">{fout}</p>}
      </CardContent>
    </Card>
  );
}
